import { Component, h, Element, Prop, Method, Event, EventEmitter, Watch, Host, State, AttachInternals } from '@stencil/core';
import 'element-internals-polyfill';

import flatpickr from 'flatpickr';
import FlatpickrLanguages from 'flatpickr/dist/l10n';
import { Instance } from 'flatpickr/dist/types/instance';
import { Hook, HookKey } from 'flatpickr/dist/types/options';
import { DatetimeMode, DatetimeType } from './datetime-interface';
import { hasSlot } from '../../utils';

/**
 *
 * @slot label - The label's text.
 *
 * @part input-group - The component's input-group element which wraps input and label.
 * @part input - The component's input element.
 * @part label - The component's label element.
 * @part suffix - The component's suffix element for icons.
 * @part button - The component's button element.
 */
@Component({
  tag: 'o2-datetime',
  styleUrl: 'datetime.scss',
  shadow: true,
  formAssociated: true,
})
export class O2Datetime {
  private input!: HTMLInputElement;
  private inputId = `o2-datetime-${inputIds++}`;
  private flatpickrInstance: Instance;

  private hasLabelSlot: boolean = false;

  private static dateOptions = {
    altFormat: 'd.m.Y',
    dateFormat: 'Y-m-d',
  };

  private static timeOptions = {
    enableTime: true,
    noCalendar: true,
    time_24hr: true,
    altFormat: 'H:i',
    dateFormat: 'H:i',
  };

  private static datetimeOptions = {
    enableTime: true,
    dateFormat: 'Y-m-dTH:i:S',
    altFormat: 'd.m.Y H:i',
  };

  @AttachInternals() internals: ElementInternals;

  @Element() private el: HTMLO2DatetimeElement;

  @State() hasValue: boolean = false;

  /**
   * Indicates error
   */
  @Prop({
    reflect: true,
  })
  error?: boolean = false;

  /**
   * Indicates warning
   */
  @Prop({
    reflect: true,
  })
  warning?: boolean = false;

  /**
   * Set initial value.
   * Format for time: hh:mm
   * Format for date: yyyy-MM-dd
   * Format for datetime: yyyy-MM-ddThh:mm:ss (ISO time)
   */
  @Prop({ mutable: true }) value?: string;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name?: string = this.inputId;

  /**
   * The label text of the input.
   */
  @Prop() label?: string;

  /**
   * Instructional text that shows before the input has a value.
   */
  @Prop() placeholder?: string;

  /**
   * Set the minimum/earliest date allowed for selection.
   * Format for date: yyyy-MM-dd
   * Format for datetime: yyyy-MM-ddThh:mm:ss (ISO time)
   */
  @Prop() min?: string;

  /**
   * Set the maximum/latest date allowed for selection.
   * Format for date: yyyy-MM-dd
   * Format for datetime: yyyy-MM-ddThh:mm:ss (ISO time)
   */
  @Prop() max?: string;

  /**
   * Type of the component.
   * Possible values: `"time"`, `"date"`, `"datetime"`.
   */
  @Prop() type?: DatetimeType = 'date';

  /**
   * Change input date format. Is is not recommented to change this format.
   */
  @Prop() dateFormat?: string;

  /**
   * If `true`, the user cannot interact with the input.
   */
  @Prop({
    reflect: true,
  })
  disabled? = false;

  /**
   * If `true`, the user cannot modify the value.
   */
  @Prop({
    reflect: true,
  })
  readonly? = false;

  /**
   * If `true`, the user must fill in a value before submitting a form.
   */
  @Prop() required? = false;

  /**
   * If `true`, the requirement text is shown. It depends on required property. If required is true, requirement text will be "(povinné)", otherwise it displays "(nepovinné)"
   */
  @Prop() showRequirementText? = false;

  /**
   * If `true`, the datetime component will be displayed with the calendar always rendered, embedded into the page among other elements
   */
  @Prop() inline? = false;

  /**
   * Icon name to show before input text.
   * @todo generate list of icons and as a type. e.g. type IconName = IconNameSecondary | IconNameSolid
   */
  @Prop() leadingIcon?: string;

  /**
   * Enable attribute to allow select range of dates from calednar.
   */
  @Prop() mode?: DatetimeMode;

  /**
   * Defines a string value that labels an interactive element
   */
  @Prop() ariaLabel?: string = undefined;

  /**
   * Defines a short hint (a word or short phrase) intended to help the user with data entry when a form control has no value
   */
  @Prop() ariaPlaceholder?: string = undefined;

  /**
   * Emitted when the value has changed.
   */
  @Event() o2Change!: EventEmitter<any>;

  /**
   * Emitted when the element loses focus.
   */
  @Event() o2Blur!: EventEmitter<any>;

  /**
   * Emitted when the datepicker is opened.
   */
  @Event() o2Open!: EventEmitter<any>;

  /**
   * Emitted when the datepicker is closed.
   */
  @Event() o2Close!: EventEmitter<any>;

  /**
   * Emitted when the month has changed.
   */
  @Event() o2MonthChange!: EventEmitter<any>;

  /**
   * Emitted when the year has changed.
   */
  @Event() o2YearChange!: EventEmitter<any>;

  /**
   * Emitted when the datepicker is ready.
   */
  @Event() o2Ready!: EventEmitter<any>;

  /**
   * Update the element when the value changes
   */
  @Watch('value')
  protected valueChanged() {
    this.flatpickrInstance.setDate(this.getValue(), true);

    this.internals.setFormValue(this.getValue(), this.value);

    this.o2Change.emit({ value: this.getValue() });
  }

  /**
   * Update the element when the error changes
   */
  @Watch('error')
  protected errorChanged() {
    let input = (this.el.shadowRoot.querySelector('input.form-control') as HTMLInputElement) || (this.el.shadowRoot.querySelector('input.flatpickr-mobile') as HTMLInputElement);
    input.classList.toggle('o2-input--error');
  }

  @Watch('readonly')
  protected readonlyChanged(newValue: boolean) {
    this.flatpickrInstance.set('clickOpens', !newValue);

    if (!newValue) {
      this.removeReadonlyFromFlatpickrInput();
    } else {
      this.addReadonlyToFlatpickrInput();
    }
  }

  /**
   * Update state when disabled attribute has changed.
   */
  @Watch('disabled')
  protected disabledChanged(value: boolean) {
    this.internals.ariaDisabled = value.toString();
  }

  /**
   * Push custom event handler for events. It is not recommented. Subscribe to desirable event instead.
   */
  @Method()
  async pushEventHandler(hookFn: Hook, name: HookKey) {
    switch (name) {
      case 'onChange':
        this.flatpickrInstance.config.onChange.push(hookFn);
        break;
      case 'onOpen':
        this.flatpickrInstance.config.onOpen.push(hookFn);
        break;
      case 'onClose':
        this.flatpickrInstance.config.onClose.push(hookFn);
        break;
      case 'onMonthChange':
        this.flatpickrInstance.config.onMonthChange.push(hookFn);
        break;
      case 'onYearChange':
        this.flatpickrInstance.config.onYearChange.push(hookFn);
        break;
    }
  }

  /**
   * Sets focus on the native `input` in `o2-input`. Use this method instead of the global
   * `input.focus()`.
   */
  @Method()
  async setFocus() {
    const input = (this.el.shadowRoot.querySelector('input.form-control') as HTMLInputElement) || (this.el.shadowRoot.querySelector('input.flatpickr-mobile') as HTMLInputElement);
    if (input) {
      input.focus();
    }
  }

  /**
   * Sets blur on the native `input` in `o2-input`. Use this method instead of the global
   * `input.blur()`.
   * @internal
   */
  @Method()
  async setBlur() {
    const input = (this.el.shadowRoot.querySelector('input.form-control') as HTMLInputElement) || (this.el.shadowRoot.querySelector('input.flatpickr-mobile') as HTMLInputElement);
    if (input) {
      input.blur();
      this.flatpickrInstance.close();
    }
  }

  private generateOptions() {
    let attrOptions = {};
    let customOptions = {};

    if (this.inline) {
      attrOptions = {
        ...attrOptions,
        inline: true,
      };
    }

    if (this.min) {
      attrOptions = {
        ...attrOptions,
        minDate: this.min,
      };
    }

    if (this.max) {
      attrOptions = {
        ...attrOptions,
        maxDate: this.max,
      };
    }

    if (this.dateFormat) {
      attrOptions = {
        ...attrOptions,
        dateFormat: this.dateFormat,
      };
    }

    switch (this.type) {
      case 'date':
        customOptions = {
          ...O2Datetime.dateOptions,
        };
        break;
      case 'time':
        customOptions = {
          ...O2Datetime.timeOptions,
        };
        break;
      case 'datetime':
        customOptions = {
          ...O2Datetime.datetimeOptions,
        };
        break;
    }

    const modeObj = this.mode ? { mode: this.mode } : {};

    customOptions = {
      ...customOptions,
      ...attrOptions,
      ...modeObj,
      altInput: true,
      allowInput: true,
      static: true,
      wrap: true,
      clickOpens: this.readonly ? false : true,
      prevArrow: "<o2-icon type='secondary' name='chevron-left' color='black' size='24'></o2-icon>",
      nextArrow: "<o2-icon type='secondary' name='chevron-right' color='black' size='24'></o2-icon>",
      onOpen: ({}, dateStr: string) => {
        this.o2Open.emit({ value: dateStr });
        this.internals.ariaExpanded = 'true';
      },
      onClose: ({}, dateStr: string) => {
        this.o2Close.emit({ value: dateStr });
        this.internals.ariaExpanded = 'false';
      },
      onMonthChange: ({}, dateStr: string, {}) => {
        this.o2MonthChange.emit({ value: dateStr });
      },
      onYearChange: ({}, dateStr: string, {}) => {
        this.o2YearChange.emit({ value: dateStr });
      },
      onValueUpdate: ({}, dateStr: string, {}) => {
        this.hasValue = !!dateStr;
        this.value = dateStr;
      },
      onReady: ({}, dateStr: string, {}) => {
        this.o2Ready.emit({ value: dateStr });

        let input =
          (this.el.shadowRoot.querySelector('input.form-control') as HTMLInputElement) || (this.el.shadowRoot.querySelector('input.flatpickr-mobile') as HTMLInputElement);
        input.setAttribute('part', 'input');

        // Set blur on Flatpickr`s created element and emit o2Blur for form validation
        input.addEventListener('blur', () => {
          this.o2Blur.emit({ value: dateStr });
        });
      },
    };

    return customOptions;
  }

  private focusInputOnClick = () => {
    this.input.focus();
  };

  componentWillLoad() {
    this.hasValue = !!this.value;

    this.internals.form && this.internals.setFormValue(this.getValue());
    this.internals.ariaLabel = this.ariaLabel;
    this.internals.ariaPlaceholder = this.ariaPlaceholder;
    this.internals.ariaReadOnly = this.readonly.toString();
    this.internals.ariaDisabled = this.disabled.toString();
    this.internals.ariaRequired = this.readonly.toString();
    this.internals.role = 'input';
    this.max ? (this.internals.ariaValueMax = this.max.toString()) : '';
    this.min ? (this.internals.ariaValueMin = this.min.toString()) : '';
  }

  /**
   * Init library
   */
  componentDidLoad() {
    flatpickr.localize(FlatpickrLanguages['sk']);

    const customOptions = this.generateOptions();

    this.flatpickrInstance = flatpickr(this.el.shadowRoot.querySelector('.o2-input-group'), customOptions);

    this.registerChangeListenerOnFlatpickrInput();

    if (this.readonly) {
      this.addReadonlyToFlatpickrInput();
    }
  }

  private getValue(): string {
    return (this.value || '').toString();
  }

  private toggle = () => {
    this.flatpickrInstance.toggle();
  };

  private registerChangeListenerOnFlatpickrInput = () => {
    const input = (this.el.shadowRoot.querySelector('input.form-control') as HTMLInputElement) || (this.el.shadowRoot.querySelector('input.flatpickr-mobile') as HTMLInputElement);

    if (input) {
      input.setAttribute('role', 'input');
      input.setAttribute('part', 'input');

      input.addEventListener('change', event => {
        const newValue = this.el.shadowRoot.querySelector('input.flatpickr-input').getAttribute('value');
        this.hasValue = !!event.target['value'];
        this.value = newValue;

        this.internals.setFormValue(this.getValue());
      });
    }
  };

  // hack: flatpickr readonly doesnt work
  // https://github.com/flatpickr/flatpickr/issues/2492
  // this method finds generated input and sets readonly attribute
  private addReadonlyToFlatpickrInput = () => {
    const input = (this.el.shadowRoot.querySelector('input.form-control') as HTMLInputElement) || (this.el.shadowRoot.querySelector('input.flatpickr-mobile') as HTMLInputElement);
    input && input.setAttribute('readonly', '');
  };

  private removeReadonlyFromFlatpickrInput = () => {
    const input = (this.el.shadowRoot.querySelector('input.form-control') as HTMLInputElement) || (this.el.shadowRoot.querySelector('input.flatpickr-mobile') as HTMLInputElement);
    input.removeAttribute('readonly');
  };

  connectedCallback() {
    // check if there is any elements as slots (children of Host element). slot without name has not the slot attribute.
    this.hasLabelSlot = hasSlot(this.el as HTMLElement, '[slot="label"]');
  }

  render() {
    const hasLabel = !!this.label || this.hasLabelSlot;

    return (
      <Host>
        <div class={`o2-input-group ${this.disabled ? 'o2-input-group--disabled' : ''} ${this.readonly ? 'o2-input-group--readonly' : ''}`} part="input-group">
          {hasLabel && (
            <label class={`o2-label ${this.error ? 'o2-label--error' : ''} ${this.warning ? 'o2-label--warning' : ''}`} htmlFor={this.inputId} part="label">
              <span class="o2-label__text" title={this.label}>
                {this.label && this.label}
                {this.hasLabelSlot && <slot name="label"></slot>}
              </span>
              {this.showRequirementText && <span class="o2-label__requirement"> {this.required ? 'Povinné' : 'Nepovinné'}</span>}
            </label>
          )}

          <div class="o2-input-container">
            {this.leadingIcon && (
              <div class="prefix" onClick={this.focusInputOnClick}>
                <o2-icon name={this.leadingIcon} size={20} color="gray-medium"></o2-icon>
              </div>
            )}

            <input
              ref={el => (this.input = el)}
              id={this.inputId}
              class={`o2-input ${this.error ? 'o2-input--error' : ''} ${this.warning ? 'o2-input--warning' : ''} ${this.leadingIcon ? 'o2-input--has-prefix' : ''} o2-input--has-suffix`}
              value={this.value}
              name={this.name}
              placeholder={this.placeholder}
              type="text"
              required={this.required}
              disabled={this.disabled}
              readonly={this.readonly}
              aria-labelledby={this.inputId}
              data-input
            />

            <div class="suffix" part="suffix">
              <div class="suffix__icon" title="Otvoriť">
                <o2-icon color="blue-brand" name={this.type == 'time' ? 'clock' : 'calendar'} size={28} safeArea={4} onClick={this.toggle}></o2-icon>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}

let inputIds = 0;
